import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { updateTradeSort } from '../../../../redux/slices/ui/tradeSort';
import {
  TradeSortMethods,
  TradeSortMethodUpdater,
  TradeSortOptionsByTime
} from './constants/tradeSortMethods';
import MixpanelService from '../../../../services/MixpanelService';
import MixpanelConfig from '../../../constants/mixpanelConfig';
import { TradeState, TradeStateName } from '../../../types/trade';
import { localStorageService } from '../../../../services/StorageService';
import { CacheKeys } from '../../../constants/filters';
import useSortMetaData from '../../list/useSortMetaData';
import { ListVariantSource } from '../../../types/auction';

const useTradeSort = (): TradeSortMethodUpdater => {
  const mixpanel = MixpanelService.get();
  const [tradeState, listVariant] = useSelector(
    state => [
      state?.ui?.nav?.secondary?.context,
      state?.ui?.layout?.listVariant
    ],
    shallowEqual
  );
  const dispatch = useDispatch();
  const { sortMethods } = useSortMetaData()?.data || {
    sortMethods: []
  };

  // Parse the metadata into a shape acceptable by the Select component
  const parsedSortMethods = useMemo(
    () =>
      sortMethods?.map(method => {
        const parsedMethod = { ...method };
        parsedMethod.label = method.value;
        parsedMethod.value = Number(method.key);
        delete parsedMethod.key;
        return parsedMethod;
      }),
    [sortMethods]
  );

  const tradeSortOptionsUpcoming = useMemo(
    () =>
      parsedSortMethods?.filter(method =>
        method.states?.includes(TradeState.upcoming)
      ),
    [parsedSortMethods]
  );

  const tradeSortOptionsActive = useMemo(
    () =>
      parsedSortMethods?.filter(method =>
        method.states?.includes(TradeState.active)
      ),
    [parsedSortMethods]
  );

  const tradeSortOptionsByTradeState = useMemo(
    () => ({
      [TradeState.active]: tradeSortOptionsActive,
      [TradeState.upcoming]: tradeSortOptionsUpcoming
    }),
    [tradeSortOptionsActive, tradeSortOptionsUpcoming]
  );

  const tradeSortOptionsByTime = useMemo(
    () =>
      parsedSortMethods?.filter(method =>
        TradeSortOptionsByTime.includes(Number(method.value))
      ),
    [parsedSortMethods]
  );

  // Push the sorting metadata into the redux store
  useEffect(() => {
    if (
      tradeSortOptionsUpcoming?.length &&
      tradeSortOptionsActive?.length &&
      tradeSortOptionsByTime?.length &&
      !localStorageService.getItem(CacheKeys.sortOptions)
    )
      localStorageService.setItem(CacheKeys.sortOptions, {
        [TradeState.active]: tradeSortOptionsActive,
        [TradeState.upcoming]: tradeSortOptionsUpcoming,
        byTime: tradeSortOptionsByTime
      });
  }, [
    tradeSortOptionsActive,
    tradeSortOptionsUpcoming,
    tradeSortOptionsByTime
  ]);

  const setSortOption = useCallback(
    (sortMethod: TradeSortMethods, noTrack?: boolean): void => {
      dispatch(
        updateTradeSort({
          sortMethod,
          tradeState
        })
      );

      const sortCache =
        localStorageService.getItem(CacheKeys.selectedSortOption) ?? {};
      sortCache[tradeState] = sortMethod;
      localStorageService.setItem(CacheKeys.selectedSortOption, sortCache);

      if (!noTrack) {
        mixpanel.track(MixpanelConfig.names.sorting, {
          list_type: ListVariantSource[listVariant],
          trade_state: TradeStateName[tradeState],
          sort_option: tradeSortOptionsByTradeState[tradeState]?.find(
            s => s.value === sortMethod
          ).label
        });
      }
    },
    [dispatch, mixpanel, tradeSortOptionsByTradeState, tradeState, listVariant]
  );

  return setSortOption;
};

export default useTradeSort;
